/* Tandoori Books: accessible type-ahead for the discovery-bar search.
   Focus stays on the input (combobox); results are real links inside listbox options. */
(() => {
  const CYRILLIC = /[Ѐ-ӿ]/;
  const LATIN = /[a-z]/i;
  const KIND_PATH = {
    product: (path) => path.includes('/products/'),
    collection: (path) => path.includes('/collections/'),
    query: (path) => /(^|\/)search\/?$/.test(path),
  };
  const IMAGE_HOSTS = ['cdn.shopify.com'];

  // Mirrors snippets/tandoori-product-title.liquid: drop a repeated Cyrillic title.
  function displayTitle(title) {
    const text = String(title ?? '');
    const parts = text.split(' | ');
    if (parts.length > 1 && CYRILLIC.test(text)) {
      const first = parts[0].trim();
      if (LATIN.test(first)) return first;
    }
    return text;
  }

  // Case-insensitive match of every query term, returned as ordered text segments.
  function highlightSegments(text, query) {
    const source = String(text ?? '');
    if (!source) return [];
    const terms = [...new Set(String(query ?? '').split(/\s+/).map(lowerSameLength).filter(Boolean))];
    if (!terms.length) return [{ text: source, match: false }];
    const lower = lowerSameLength(source);
    const marked = new Array(source.length).fill(false);
    for (const term of terms) {
      for (let index = lower.indexOf(term); index !== -1; index = lower.indexOf(term, index + term.length)) {
        marked.fill(true, index, index + term.length);
      }
    }
    const segments = [];
    for (let index = 0; index < source.length; index += 1) {
      const last = segments[segments.length - 1];
      if (last && last.match === marked[index]) last.text += source[index];
      else segments.push({ text: source[index], match: marked[index] });
    }
    return segments;
  }

  // Lower-case per code point without changing the string length, so indexes line up.
  function lowerSameLength(value) {
    let lower = '';
    for (const character of String(value)) {
      const mapped = character.toLowerCase();
      lower += mapped.length === character.length ? mapped : character;
    }
    return lower;
  }

  // Same-origin storefront path (with query) for a result of the given kind, or null.
  function resultHref(url, origin, kind) {
    const matchesKind = KIND_PATH[kind];
    if (!matchesKind || url === undefined || url === null || url === '') return null;
    try {
      const base = new URL(origin);
      const parsed = new URL(String(url), base);
      if (parsed.origin !== base.origin || !/^https?:$/.test(parsed.protocol)) return null;
      if (!matchesKind(parsed.pathname)) return null;
      return parsed.pathname + parsed.search;
    } catch {
      return null;
    }
  }

  function isSafeResultUrl(url, origin, kind) {
    return resultHref(url, origin, kind) !== null;
  }

  // Cover images only from this storefront or Shopify's HTTPS CDN.
  function safeImageUrl(src, origin, width) {
    if (!src) return null;
    try {
      const value = String(src);
      const parsed = new URL(value.startsWith('//') ? `https:${value}` : value, origin);
      const sameOrigin = parsed.origin === new URL(origin).origin;
      if (!sameOrigin && !(parsed.protocol === 'https:' && IMAGE_HOSTS.includes(parsed.hostname))) return null;
      if (width) parsed.searchParams.set('width', String(width));
      return parsed.href;
    } catch {
      return null;
    }
  }

  // Predictive search prices are major-unit decimal strings, e.g. "12.99".
  function formatPrice(value, locale, currency) {
    const raw = value === undefined || value === null ? '' : String(value);
    const amount = Number(raw);
    if (raw === '' || !Number.isFinite(amount)) return raw;
    try {
      return new Intl.NumberFormat(locale || 'en', { style: 'currency', currency: currency || 'USD' }).format(amount);
    } catch {
      return raw;
    }
  }

  // Insert the shopper's query literally: a replacer function keeps "$&", "$'" and "$$" as typed.
  function withTerms(template, query) {
    return String(template).replace('[terms]', () => query);
  }

  if (typeof module !== 'undefined' && module.exports) {
    module.exports = { displayTitle, highlightSegments, resultHref, isSafeResultUrl, safeImageUrl, formatPrice, withTerms };
    return;
  }

  const form = document.querySelector('[data-tb-live-search]');
  if (!form) return;

  const input = form.querySelector('input[type="search"]');
  const panel = form.querySelector('[data-tb-search-panel]');
  const listbox = panel?.querySelector('[role="listbox"]');
  const message = panel?.querySelector('[data-tb-search-message]');
  const status = form.querySelector('[data-tb-search-status]');
  if (!input || !panel || !listbox || !message || !status) return;

  const text = form.dataset;
  // English fallbacks cover a locale that is missing one of these strings.
  const label = (key, fallback) => {
    const value = text[key];
    return value && !/^translation missing/i.test(value) ? value : fallback;
  };
  const locale = document.documentElement.lang || 'en';
  const currency = window.Shopify?.currency?.active || 'USD';
  const origin = window.location.origin;
  const SVG_NS = 'http://www.w3.org/2000/svg';
  const ICONS = {
    search: 'M10.5 17a6.5 6.5 0 1 0 0-13 6.5 6.5 0 0 0 0 13ZM20 20l-4.8-4.8',
    arrow: 'M5 12h14M13 6l6 6-6 6',
    shelf: 'M4 5v14M9 5v14M14 6l4 13M3 19h18',
    chat: 'M5 5h14v10H9l-4 4V5Z',
  };

  let timer;
  let revealTimer;
  let controller;
  let waiting = false;
  let requestId = 0;
  let optionCount = 0;
  let options = [];
  let activeIndex = -1;
  let renderedQuery = '';

  const icon = (name) => {
    const svg = document.createElementNS(SVG_NS, 'svg');
    svg.setAttribute('viewBox', '0 0 24 24');
    svg.setAttribute('width', '18');
    svg.setAttribute('height', '18');
    svg.setAttribute('aria-hidden', 'true');
    svg.setAttribute('focusable', 'false');
    svg.setAttribute('class', `tb-search-suggestions__icon tb-search-suggestions__icon--${name}`);
    const path = document.createElementNS(SVG_NS, 'path');
    path.setAttribute('d', ICONS[name]);
    path.setAttribute('fill', 'none');
    path.setAttribute('stroke', 'currentColor');
    path.setAttribute('stroke-width', '1.7');
    path.setAttribute('stroke-linecap', 'round');
    path.setAttribute('stroke-linejoin', 'round');
    svg.append(path);
    return svg;
  };

  const element = (tag, className, content) => {
    const node = document.createElement(tag);
    if (className) node.className = className;
    if (content !== undefined) node.append(content);
    return node;
  };

  const highlighted = (value, query) => {
    const fragment = document.createDocumentFragment();
    for (const segment of highlightSegments(value, query)) {
      fragment.append(segment.match ? element('mark', '', segment.text) : document.createTextNode(segment.text));
    }
    return fragment;
  };

  const searchUrl = (query) => {
    const url = new URL(form.getAttribute('action') || '/search', origin);
    url.searchParams.set('q', query);
    url.searchParams.set('type', 'product');
    return url.pathname + url.search;
  };

  const createOption = (href, modifier, children) => {
    const option = element('li', `tb-search-suggestions__option tb-search-suggestions__option--${modifier}`);
    option.id = `TandooriSearchOption-${(optionCount += 1)}`;
    option.setAttribute('role', 'option');
    option.setAttribute('aria-selected', 'false');
    const link = element('a', 'tb-search-suggestions__link');
    link.href = href;
    link.tabIndex = -1;
    link.append(...children);
    option.append(link);
    return option;
  };

  // A labelled group of options; the visible heading is not an option itself.
  const createGroup = (key, heading, items) => {
    const group = element('ul', `tb-search-suggestions__group tb-search-suggestions__group--${key}`);
    const title = element('li', 'tb-search-suggestions__heading', heading);
    title.id = `TandooriSearchGroup-${key}`;
    title.setAttribute('role', 'presentation');
    group.setAttribute('role', 'group');
    group.setAttribute('aria-labelledby', title.id);
    group.append(title, ...items);
    return group;
  };

  const thumbnail = (image, modifier) => {
    const src = safeImageUrl(image?.url || image, origin, 120);
    if (!src) return element('span', `tb-search-suggestions__thumb tb-search-suggestions__thumb--empty tb-search-suggestions__thumb--${modifier}`);
    const img = element('img', `tb-search-suggestions__thumb tb-search-suggestions__thumb--${modifier}`);
    img.src = src;
    img.alt = '';
    img.loading = 'lazy';
    img.decoding = 'async';
    img.width = 40;
    img.height = modifier === 'product' ? 54 : 40;
    return img;
  };

  const productOption = (product, query) => {
    const href = resultHref(product?.url, origin, 'product');
    const title = displayTitle(product?.title).trim();
    if (!href || !title) return null;

    const details = element('span', 'tb-search-suggestions__text');
    details.append(element('span', 'tb-search-suggestions__title', highlighted(title, query)));

    const meta = element('span', 'tb-search-suggestions__meta');
    const shopName = (text.shopName || '').trim().toLowerCase();
    const vendor = String(product.vendor || '').trim();
    const kind = String(product.type || '').trim() || (vendor.toLowerCase() !== shopName ? vendor : '');
    if (kind) meta.append(element('span', 'tb-search-suggestions__kind', kind));

    const varies = product.price_min !== undefined && product.price_max !== undefined && String(product.price_min) !== String(product.price_max);
    const amount = formatPrice(varies ? product.price_min : product.price ?? product.price_min, locale, currency);
    if (amount) meta.append(element('span', 'tb-search-suggestions__price', varies ? `${label('labelFrom', 'From')} ${amount}` : amount));
    if (product.available === false) meta.append(element('span', 'tb-search-suggestions__badge', label('labelSoldOut', 'Out of stock')));
    if (meta.childElementCount) details.append(meta);

    return createOption(href, 'product', [thumbnail(product.featured_image?.url ? product.featured_image : product.image, 'product'), details]);
  };

  const querySuggestion = (suggestion, query) => {
    const value = String(suggestion?.text || '').trim();
    const href = resultHref(suggestion?.url, origin, 'query');
    if (!value || !href || value.toLowerCase() === query.toLowerCase()) return null;
    const url = new URL(href, origin);
    url.searchParams.set('type', 'product');
    return createOption(url.pathname + url.search, 'query', [
      icon('search'),
      element('span', 'tb-search-suggestions__title', highlighted(value, query)),
    ]);
  };

  const collectionOption = (collection, query) => {
    const href = resultHref(collection?.url, origin, 'collection');
    const title = String(collection?.title || '').trim();
    if (!href || !title) return null;
    const image = safeImageUrl(collection.featured_image?.url, origin, 120);
    return createOption(href, 'collection', [
      image ? thumbnail(collection.featured_image, 'collection') : element('span', 'tb-search-suggestions__glyph', icon('shelf')),
      element('span', 'tb-search-suggestions__title', highlighted(title, query)),
    ]);
  };

  const take = (items, limit, build, query) => {
    const built = [];
    for (const item of Array.isArray(items) ? items : []) {
      if (built.length === limit) break;
      const option = build(item, query);
      if (option) built.push(option);
    }
    return built;
  };

  const setActive = (index, scroll = true) => {
    options[activeIndex]?.setAttribute('aria-selected', 'false');
    options[activeIndex]?.classList.remove('is-active');
    activeIndex = options[index] ? index : -1;
    const option = options[activeIndex];
    if (!option) {
      input.removeAttribute('aria-activedescendant');
      return;
    }
    option.setAttribute('aria-selected', 'true');
    option.classList.add('is-active');
    input.setAttribute('aria-activedescendant', option.id);
    if (!scroll) return;
    const bounds = panel.getBoundingClientRect();
    const rect = option.getBoundingClientRect();
    if (rect.top < bounds.top + 8) panel.scrollTop -= bounds.top + 8 - rect.top;
    else if (rect.bottom > bounds.bottom - 8) panel.scrollTop += rect.bottom - bounds.bottom + 8;
  };

  const setOpen = (open) => {
    if (!open) setActive(-1);
    panel.hidden = !open;
    input.setAttribute('aria-expanded', String(open));
  };

  const stopLoading = () => {
    waiting = false;
    clearTimeout(revealTimer);
    panel.classList.remove('is-loading');
    listbox.removeAttribute('aria-busy');
  };

  const clearResults = () => {
    listbox.replaceChildren();
    message.replaceChildren();
    message.hidden = true;
    options = [];
    activeIndex = -1;
    renderedQuery = '';
    input.removeAttribute('aria-activedescendant');
  };

  const showMessage = (modifier, lines) => {
    message.className = `tb-search-suggestions__message tb-search-suggestions__message--${modifier}`;
    message.replaceChildren(...lines);
    message.hidden = false;
  };

  const render = (query, results) => {
    clearResults();
    const queries = take(results.queries, 4, querySuggestion, query);
    const products = take(results.products, 6, productOption, query);
    const collections = take(results.collections, 3, collectionOption, query);
    const groups = [];
    if (queries.length) groups.push(createGroup('queries', label('labelSuggestions', 'Suggestions'), queries));
    if (products.length) groups.push(createGroup('products', label('labelProducts', 'Products'), products));
    if (collections.length) groups.push(createGroup('collections', label('labelCollections', 'Collections'), collections));
    const count = queries.length + products.length + collections.length;

    if (count) {
      const all = createOption(searchUrl(query), 'all', [
        element('span', 'tb-search-suggestions__title', withTerms(label('labelSeeAll', 'See all results for “[terms]”'), query)),
        icon('arrow'),
      ]);
      const footer = element('ul', 'tb-search-suggestions__group tb-search-suggestions__group--footer', all);
      footer.setAttribute('role', 'none');
      groups.push(footer);
      status.textContent = label('labelResultsCount', '[count] suggestions available').replace('[count]', String(count));
    } else {
      showMessage('empty', [
        element('p', 'tb-search-suggestions__message-title', withTerms(label('labelNoResults', 'No results for “[terms]”'), query)),
        element('p', 'tb-search-suggestions__message-help', label('labelNoResultsHelp', 'Check the spelling, or try an author, a series, or the Latin or Cyrillic spelling.')),
      ]);
      const help = [createOption(searchUrl(query), 'action', [icon('search'), element('span', 'tb-search-suggestions__title', label('labelSearchAll', 'Search all books'))])];
      const helpUrl = String(text.helpUrl || '');
      if (/^https:\/\//.test(helpUrl)) help.push(createOption(helpUrl, 'action', [icon('chat'), element('span', 'tb-search-suggestions__title', label('labelAsk', 'Ask our booksellers'))]));
      const actions = element('ul', 'tb-search-suggestions__group tb-search-suggestions__group--actions');
      actions.setAttribute('role', 'none');
      actions.append(...help);
      groups.push(actions);
      status.textContent = withTerms(label('labelNoResults', 'No results for “[terms]”'), query);
    }

    listbox.append(...groups);
    options = [...listbox.querySelectorAll('[role="option"]')];
    renderedQuery = query;
    panel.scrollTop = 0;
  };

  // Results only open the panel while someone is still typing in the field.
  const inputFocused = () => document.activeElement === input;

  const search = async (query, activateFirst = false) => {
    controller?.abort();
    controller = new AbortController();
    const current = ++requestId;
    // Pending until stopLoading() or cancelPending(), so the first Escape cancels instead of clearing.
    waiting = true;
    panel.classList.add('is-loading');
    listbox.setAttribute('aria-busy', 'true');
    clearTimeout(revealTimer);
    if (panel.hidden) {
      // Reveal a loading-only panel for slow responses; fast ones never flash.
      revealTimer = setTimeout(() => {
        if (current !== requestId || !inputFocused()) return;
        clearResults();
        showMessage('quiet', [element('p', 'tb-search-suggestions__message-help', label('labelLoading', 'Searching…'))]);
        setOpen(true);
      }, 300);
    }

    // routes.predictive_search_url keeps any locale or market prefix, e.g. /uz/search/suggest.
    const url = new URL(text.suggestUrl || '/search/suggest.json', origin);
    url.searchParams.set('q', query);
    url.searchParams.set('resources[type]', 'product,query,collection');
    url.searchParams.set('resources[limit]', '6');
    url.searchParams.set('resources[limit_scope]', 'each');
    url.searchParams.set('resources[options][fields]', 'title,tag,product_type,variants.title,variants.sku,vendor');
    url.searchParams.set('resources[options][unavailable_products]', 'last');

    try {
      const response = await fetch(url, { signal: controller.signal, headers: { Accept: 'application/json' } });
      if (!response.ok) throw new Error(`Search suggestions failed: ${response.status}`);
      const data = await response.json();
      if (current !== requestId || query !== input.value.trim()) return;
      stopLoading();
      render(query, data?.resources?.results || {});
      if (!inputFocused()) return;
      setOpen(true);
      if (activateFirst) setActive(0);
    } catch (error) {
      if (error?.name === 'AbortError' || current !== requestId) return;
      stopLoading();
      clearResults();
      renderedQuery = query;
      const unavailable = label('labelUnavailable', 'Suggestions are unavailable right now. Press Enter to search.');
      showMessage('quiet', [element('p', 'tb-search-suggestions__message-help', unavailable)]);
      status.textContent = unavailable;
      if (inputFocused()) setOpen(true);
    }
  };

  // Drop a pending or in-flight request so late results cannot reopen a closed panel.
  const cancelPending = () => {
    clearTimeout(timer);
    controller?.abort();
    requestId += 1;
    stopLoading();
  };

  const reset = () => {
    cancelPending();
    setOpen(false);
    clearResults();
    status.textContent = '';
  };

  input.addEventListener('input', () => {
    cancelPending();
    setActive(-1);
    const query = input.value.trim();
    if (!query) {
      reset();
      return;
    }
    if (!panel.hidden) {
      // Keep the previous suggestions visible, dimmed, until the new ones arrive.
      panel.classList.add('is-loading');
      listbox.setAttribute('aria-busy', 'true');
    }
    waiting = true;
    timer = setTimeout(() => search(query), 180);
  });

  input.addEventListener('focus', () => {
    const query = input.value.trim();
    if (query && query === renderedQuery && (options.length || !message.hidden)) setOpen(true);
  });

  input.addEventListener('keydown', (event) => {
    if (event.isComposing) return;
    const query = input.value.trim();
    switch (event.key) {
      case 'ArrowDown':
        if (panel.hidden) {
          if (!query) return;
          event.preventDefault();
          if (query === renderedQuery && options.length) {
            setOpen(true);
            setActive(0);
          } else {
            cancelPending();
            search(query, true);
          }
          return;
        }
        if (!options.length) return;
        event.preventDefault();
        setActive(activeIndex < options.length - 1 ? activeIndex + 1 : 0);
        return;
      case 'ArrowUp':
        if (panel.hidden || !options.length) return;
        event.preventDefault();
        setActive(activeIndex > 0 ? activeIndex - 1 : options.length - 1);
        return;
      case 'Enter': {
        const link = !panel.hidden && options[activeIndex]?.querySelector('a');
        if (!link) return; // No active option: submit the full search.
        event.preventDefault();
        if (event.metaKey || event.ctrlKey) window.open(link.href, '_blank', 'noopener');
        else window.location.assign(link.href);
        return;
      }
      case 'Escape':
        // First Escape closes (and cancels a pending search); the next one clears the field.
        if (!panel.hidden || waiting) {
          event.preventDefault();
          cancelPending();
          setOpen(false);
        } else if (input.value) {
          event.preventDefault();
          input.value = '';
          reset();
        }
        return;
      case 'Tab':
        cancelPending();
        setOpen(false);
        return;
      default:
    }
  });

  // Keep focus on the input while choosing with a pointer; the click still follows the link.
  panel.addEventListener('mousedown', (event) => {
    if (event.button === 0) event.preventDefault();
  });
  listbox.addEventListener('pointermove', (event) => {
    const option = event.target.closest?.('[role="option"]');
    const index = option ? options.indexOf(option) : -1;
    if (index !== -1 && index !== activeIndex) setActive(index, false);
  });

  document.addEventListener('pointerdown', (event) => {
    if (!form.contains(event.target)) setOpen(false);
  });
  form.addEventListener('focusout', () => {
    setTimeout(() => {
      if (!form.contains(document.activeElement)) setOpen(false);
    }, 0);
  });
  window.addEventListener('pageshow', (event) => {
    if (event.persisted) setOpen(false);
  });
})();
